import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldOff, Copy, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

/** Full 2FA management surface: status, setup (QR + backup codes), disable. */
export default function TwoFactorSetup({ open, onOpenChange }) {
  const [status, setStatus] = useState(null); // { enabled }
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null); // { qr_data_url, backup_codes }
  const [otp, setOtp] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  useEffect(() => {
    if (!open) return;
    setSetupData(null);
    setOtp("");
    setShowDisable(false);
    api.get("/2fa/status").then(r => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, [open]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/setup");
      setSetupData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit code from your app");
    setBusy(true);
    try {
      await api.post("/2fa/verify-setup", { code: otp });
      toast.success("2FA is now enabled");
      setStatus({ enabled: true });
      setSetupData(null);
      setOtp("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode) return toast.error("Enter your authenticator code or a backup code");
    setBusy(true);
    try {
      await api.post("/2fa/disable", { password: disablePw, code: disableCode });
      toast.success("2FA disabled");
      setStatus({ enabled: false });
      setShowDisable(false);
      setDisablePw("");
      setDisableCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.backup_codes.join("\n"));
    toast.success("Backup codes copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="two-factor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Two-factor authentication
          </DialogTitle>
          <DialogDescription>
            Protect your account with a 6-digit code from an authenticator app
            (Google Authenticator, Authy, 1Password, Microsoft Authenticator).
          </DialogDescription>
        </DialogHeader>

        {status === null && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking status…
          </div>
        )}

        {status?.enabled && !showDisable && (
          <div className="space-y-3 py-2">
            <Alert>
              <ShieldCheck className="w-4 h-4" />
              <AlertDescription>
                <Badge variant="default" className="mr-1">Enabled</Badge>
                Your account is protected. You'll be asked for a 6-digit code
                on every sign-in.
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={() => setShowDisable(true)}
              data-testid="disable-2fa-btn"
              className="w-full"
            >
              <ShieldOff className="w-4 h-4 mr-2" /> Disable 2FA
            </Button>
          </div>
        )}

        {status?.enabled && showDisable && (
          <div className="space-y-3 py-2">
            <Label htmlFor="dis-pw" className="text-xs">Current password</Label>
            <Input
              id="dis-pw" type="password" value={disablePw}
              onChange={(e) => setDisablePw(e.target.value)}
              data-testid="disable-2fa-pw"
              placeholder="Leave blank if you signed up with Google"
            />
            <Label htmlFor="dis-code" className="text-xs">Authenticator or backup code</Label>
            <Input
              id="dis-code" value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.trim())}
              placeholder="123456 or one backup code"
              data-testid="disable-2fa-code"
            />
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowDisable(false)} className="flex-1">Cancel</Button>
              <Button variant="destructive" onClick={disable2FA} disabled={busy} className="flex-1" data-testid="disable-2fa-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable"}
              </Button>
            </div>
          </div>
        )}

        {status?.enabled === false && !setupData && (
          <div className="space-y-3 py-2">
            <Alert>
              <AlertDescription>
                2FA is currently <Badge variant="secondary">off</Badge>. Set it up
                once, then you'll enter a code from your phone each time you sign in.
              </AlertDescription>
            </Alert>
            <Button onClick={startSetup} disabled={busy} className="w-full" data-testid="start-2fa-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {busy ? "Generating…" : "Set up 2FA"}
            </Button>
          </div>
        )}

        {setupData && (
          <div className="space-y-3 py-1">
            <div className="text-sm font-medium">1. Scan this QR in your authenticator app</div>
            <div className="flex justify-center bg-white p-3 rounded-lg border">
              <img src={setupData.qr_data_url} alt="2FA QR" className="w-48 h-48" data-testid="2fa-qr" />
            </div>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Can't scan? Enter this key manually</summary>
              <code className="block mt-1 break-all bg-muted p-2 rounded">{setupData.provisioning_uri}</code>
            </details>

            <div className="text-sm font-medium pt-2 flex items-center justify-between">
              <span>2. Save these backup codes</span>
              <Button variant="ghost" size="sm" onClick={copyCodes} data-testid="copy-backup-codes">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy all
              </Button>
            </div>
            <pre className="text-xs bg-muted rounded p-3 leading-relaxed font-mono grid grid-cols-2 gap-x-3">
              {setupData.backup_codes.map((c, i) => <span key={i}>{c}</span>)}
            </pre>
            <p className="text-[11px] text-muted-foreground">
              Each code works once. Store them somewhere safe — you won't see them again.
            </p>

            <div className="text-sm font-medium pt-2">3. Enter the current 6-digit code</div>
            <Input
              inputMode="numeric" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              data-testid="setup-2fa-code"
              autoFocus
            />
            <Button
              onClick={confirmSetup}
              disabled={busy || otp.length !== 6}
              className="w-full"
              data-testid="confirm-2fa-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Activate 2FA
            </Button>
          </div>
        )}

        {!setupData && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
